import type { CellValue, SourceColumn, SourceColumnKind } from "@zebrabi/office-types";
import type { Field } from "@zebrabi/fields-pane";

/** A cell as the hosts hand it over: a `CellValue`, or nothing for a cell that is not there */
export type SourceCell = CellValue | null | undefined;

/**
 * Lists a source's columns for the Fields list: one entry per header cell, in source order, with the kind its cells
 * decide. A column is text when every cell below the header passes `isTextCell`, measure otherwise, which is the
 * rule the parsers use to find the category and comment columns, so the list agrees with what they do.
 * @param headerRow the first row of the source, one cell per column
 * @param dataRows the rows below the header; a short row counts as empty for the columns it lacks
 * @param isTextCell whether a cell is text rather than a number; each host passes the rule its cells follow
 * @param categoryColumnsCount the leading columns the parser always reads as categories; they are text whatever their cells hold
 */
export function classifySourceColumns(
    headerRow: readonly SourceCell[],
    dataRows: readonly (readonly SourceCell[])[],
    isTextCell: (cell: SourceCell) => boolean,
    categoryColumnsCount = 0
): SourceColumn[] {
    return headerRow.map((header: SourceCell, index: number): SourceColumn => {
        // a blank or missing header is an empty name
        const name = String(header ?? "").trim();

        const cellsBelow = dataRows.map((row: readonly SourceCell[]) => row[index]);
        const isCategory = index < categoryColumnsCount;
        const isText = isCategory || cellsBelow.every(isTextCell);
        const kind: SourceColumnKind = isText ? "text" : "measure";

        return { index, name, kind };
    });
}

/** Excel's rule: a cell read from a range is text when it is a string, so an empty cell counts as text */
export function isExcelTextCell(cell: SourceCell): cell is string {
    return typeof cell === "string";
}

/** The data editor's rule: every cell is a string there, so a cell is text when it does not parse as a number; an empty cell counts as text */
export function isSheetTextCell(cell: SourceCell): boolean {
    const isMissing = cell === undefined || cell === null;
    const text = isMissing ? "" : String(cell);

    const isEmpty = text === "";
    // the grid's value parser reads a cell with parseFloat after dropping thousands separators, so "1,200" and "5%" are numbers there too
    const isNumber = !isNaN(parseFloat(text.replace(/,/g, "")));

    return isEmpty || !isNumber;
}

/** A reader column as the switchers see it: a measure carries its index among the value fields, a category level none */
export interface ReaderColumn {
    displayName: string;
    index?: number;
}

/**
 * A value column's kind from its cells. The parsers type a column by its role, so a number column in Comments
 * is typed as text; the cells are the only reliable source. Without cells, a pivot table or an older document,
 * every value column is a number.
 */
export function valueColumnKind(sourceIndex: number | undefined, sourceColumns: readonly SourceColumn[] | undefined): SourceColumnKind {
    const sourceColumn = sourceIndex === undefined ? undefined : sourceColumns?.[sourceIndex];

    return sourceColumn?.kind ?? "measure";
}

/** A pane field the switcher built from a reader column, with the index of the source column it stands for when one is known */
export interface ReaderField {
    field: Field;
    sourceIndex: number | undefined;
}

/**
 * The source column a measure column was read from, as an index into `sourceColumns`. Every flat source reads its
 * value fields from the columns after the category columns, in order, so value field `k` is source column
 * `categoryColumnsCount + k`. The names have to agree; when they do not (a reader that lists no category columns, a
 * grid that dropped a blank header), the one source column with the same name is taken instead. A column the parser
 * made up (the "Values" of a cross table) maps to nothing rather than to the wrong column.
 */
export function sourceIndexOf(
    column: ReaderColumn,
    sourceColumns: readonly SourceColumn[] | undefined,
    categoryColumnsCount: number
): number | undefined {
    if (column.index === undefined || !sourceColumns) {
        return undefined;
    }

    const name = column.displayName.trim();
    const guess = categoryColumnsCount + column.index;
    const guessMatches = sourceColumns[guess]?.name === name;
    if (guessMatches) {
        return guess;
    }

    // a header name used once identifies the column on its own
    const sameName = sourceColumns.filter((candidate: SourceColumn) => candidate.name === name);

    return sameName.length === 1 ? sameName[0].index : undefined;
}

/**
 * The Fields list of a flat source: one field per source column, in source order. A source column the reader kept is
 * the reader's own field, so it keeps its id, its role and the bucket it sits in, with the kind the cells decide. One
 * the reader skipped is listed without a role and fixed. A reader field that
 * stands for no source column (the "Values" of a cross table) goes after the source columns.
 */
export function fieldsListOf(sourceColumns: readonly SourceColumn[], readerFields: readonly ReaderField[]): Field[] {
    const listed = sourceColumns.map((sourceColumn: SourceColumn): Field => {
        const readerField = readerFields.find((candidate: ReaderField) => candidate.sourceIndex === sourceColumn.index);
        if (readerField) {
            // the cells know better than the role: a number column the reader put into Comments is still a number column
            return { ...readerField.field, kind: sourceColumn.kind };
        }

        // a blank header still needs a label the user can find in the sheet
        const label = sourceColumn.name || `Column ${sourceColumn.index + 1}`;

        return { id: `source:${sourceColumn.index}`, label, kind: sourceColumn.kind, role: null, movable: false };
    });

    const unmatched = readerFields
        .filter((candidate: ReaderField) => candidate.sourceIndex === undefined)
        .map((candidate: ReaderField) => candidate.field);

    return [...listed, ...unmatched];
}
